import { z } from "zod"

const point = z.tuple([z.number().finite(), z.number().finite(), z.number().finite()])

/** Point de vue du rendu realiste : celui de la vue 3D (metres, axe Y vers le haut), ou null pour la vue automatique */
export const renderCameraSchema = z
  .object({
    position: point,
    target: point,
    /** Angle de champ vertical, en degres */
    fov: z.number().min(10).max(120),
  })
  .nullable()

/** Les points de vue enregistres d'un projet (FIA-E06) : un nom de 60 caracteres au plus, dix par projet au plus */
export const VIEWPOINT_LIMITS = { nameMax: 60, perProject: 10 } as const

/** Deux noms de points de vue sont les memes a la casse et aux blancs de bord pres */
export const sameViewpointName = (a: string, b: string) => a.trim().toLocaleLowerCase('fr') === b.trim().toLocaleLowerCase('fr')

/** Un autre point de vue que exceptId porte-t-il deja ce nom ? */
export const viewpointNameTaken = (viewpoints: { id: string; name: string }[], name: string, exceptId?: string) =>
  viewpoints.some(v => v.id !== exceptId && sameViewpointName(v.name, name))

/** Pourquoi un nom est refuse — vide, trop long, deja pris par un autre point de vue que exceptId —, ou null.
 *  La regle unique : le reducteur, la fenetre de rendu et l'editeur l'appliquent tous trois */
export function viewpointRefusal(viewpoints: { id: string; name: string }[], name: string, exceptId?: string): string | null {
  const trimmed = name.trim()
  if (!trimmed) return 'Donnez un nom au point de vue.'
  if (trimmed.length > VIEWPOINT_LIMITS.nameMax) return `${VIEWPOINT_LIMITS.nameMax} caractères au plus.`
  if (viewpointNameTaken(viewpoints, trimmed, exceptId)) return 'Ce nom existe déjà'
  return null
}

export const viewpointNameSchema = z.string().trim().min(1).max(VIEWPOINT_LIMITS.nameMax)

/** Un point de vue enregistre : la camera de la vue 3D (jamais la vue automatique), sous un nom */
export const projectViewpointSchema = z.object({
  id: z.string().min(1),
  name: viewpointNameSchema,
  camera: z.object({
    position: point,
    target: point,
    fov: z.number().min(10).max(120),
  }),
  createdAt: z.string(),
})
export type ProjectViewpointInput = z.infer<typeof projectViewpointSchema>

/** A la sauvegarde du projet : la forme de chaque point de vue. Le nombre (dix) et l'unicite des noms sont les regles de
 *  l'editeur (viewpointRefusal, le reducteur) : la fusion de deux versions du projet peut les depasser, et la sauvegarde
 *  ne doit pas se bloquer pour autant — l'editeur montre alors la limite, et chacun se renomme ou se supprime */
export const savedViewpointsSchema = z.array(projectViewpointSchema)

/** Sur une demande de rendu : le nom du point de vue enregistre, ou null (la vue 3D du moment, la vue automatique) */
export const renderViewpointNameSchema = viewpointNameSchema.nullable().default(null)

/** Orientation du projet pour le rendu : le nord (degres, sens horaire depuis le haut du plan), ou null sans orientation */
export const renderOrientationSchema = z
  .object({
    northDeg: z.number().finite(),
  })
  .nullable()

/** Qualite du rendu : standard (1280 x 720, une minute environ) ou haute (1920 x 1080, brins d'herbe en relief, environ cinq minutes) */
export const renderQualitySchema = z.enum(["standard", "haute"]).default("standard")
export type RenderQuality = z.infer<typeof renderQualitySchema>

/** Calcul du rendu : serveur (processeur du serveur, gratuit) ou gpu (carte graphique chez Modal, admins seulement — GPU) */
export const renderEngineSchema = z.enum(["serveur", "gpu"]).default("serveur")
export type RenderEngine = z.infer<typeof renderEngineSchema>

/** Moteur de la finition IA du decor (FIA) : la maison reste l'image de Blender, l'IA repeint le jardin et le ciel */
export const renderAiModelSchema = z.enum(["sdxl", "qwen", "sd35", "flux_schnell"])
export type RenderAiModel = z.infer<typeof renderAiModelSchema>

/** La finition qu'une demande peut porter : seul Qwen est garde apres l'essai (decision de Boris du 2026-11-02, FIA) */
export const renderFinishRequestSchema = z.enum(["qwen"]).nullable().default(null)
/** Les passes qu'un admin peut choisir sur la carte graphique, pour comparer (FIX-101) ; 1000 : le reglage de la Haute */
export const RENDER_SAMPLES_CHOICES = [400, 1000, 2000] as const
export const RENDER_DEFAULT_GPU_HQ_SAMPLES = 1000
/** Le champ du formulaire : absent ou vide, null (le reglage de la qualite) ; sinon l'un des choix */
export const renderSamplesSchema = z
  .union([z.literal(""), z.null(), z.undefined(), z.coerce.number().int()])
  .transform(v => (v === "" || v === null || v === undefined ? null : v))
  .refine(v => v === null || (RENDER_SAMPLES_CHOICES as readonly number[]).includes(v))

/** La reponse a une demande de rendu : son rang dans la file du serveur (null sur la carte graphique) et son calcul */
export interface RenderRequestOutcome {
  id: string
  position: number | null
  engine: RenderEngine
}

/** Etat d'une demande de rendu dans la file */
export const renderStatusSchema = z.enum(["en_attente", "en_cours", "termine", "echoue"])
export type RenderStatus = z.infer<typeof renderStatusSchema>

/** Resultat renvoye par le service de rendu : l'image (JPEG en base64), avec les passes de la finition IA (zip en
 *  base64) quand la demande en porte une, ou l'echec */
export const renderResultSchema = z.union([
  z.object({ image_base64: z.string().min(1), passes_base64: z.string().min(1).optional() }),
  z.object({ error: z.string().min(1).max(2000) }),
])
export type RenderResult = z.infer<typeof renderResultSchema>

/** Resultat de la finition IA renvoye par Modal : l'image finie (JPEG en base64) ou l'echec */
export const renderFinitionResultSchema = z.union([
  z.object({ image_base64: z.string().min(1) }),
  z.object({ error: z.string().min(1).max(2000) }),
])
export type RenderFinitionResult = z.infer<typeof renderFinitionResultSchema>

/** Taille maximale de la maquette envoyee au rendu */
export const RENDER_MAX_GLB_BYTES = 20 * 1024 * 1024

/** Une demande de rendu telle que la fenetre la suit (sans maquette ni image) */
export interface RenderJobView {
  id: string
  status: RenderStatus
  quality: RenderQuality
  /** Calcul prevu ou en cours */
  engine: RenderEngine
  /** Demandee sur la carte graphique, calculee sur le serveur */
  gpuFallback: boolean
  /** Finition IA du decor (FIA), ou null */
  aiModel: RenderAiModel | null
  /** Finition IA demandee, en echec : l'image est celle de Blender */
  aiFallback: boolean
  /** Le point de vue enregistre au moment de la demande (FIA-E06), ou null */
  viewpointName: string | null
  /** Sans camera : la vue automatique (demande lancee depuis la 2D sans point de vue) */
  automaticView: boolean
  /** En cours de finition IA : Blender a fini */
  stage?: "finition"
  /** Passes choisies par un admin (FIX-101) ; absent : le reglage de la qualite */
  samples?: number
  /** Rang dans la file du serveur (1 : la prochaine), pour une demande en attente sur le serveur */
  position?: number
  createdAt: string
  /** Debut du calcul (prise par le serveur ou la carte graphique) */
  startedAt?: string
  finishedAt?: string
  userName?: string
  /** Demandee par l'utilisateur qui lit la liste */
  mine: boolean
  /** Pret, et pas encore vu par son auteur (qui lit la liste) */
  isNew: boolean
  /** L'utilisateur qui lit la liste peut le supprimer (son auteur, ou le proprietaire du projet) */
  canDelete: boolean
  error?: string
}

/** Evenement publie par Mercure a l'auteur d'une demande, a chaque changement d'etat */
export interface RenderEvent {
  id: string
  projectId: string
  /** supprime : la demande a ete supprimee (par son auteur ou le proprietaire du projet) */
  status: RenderStatus | "supprime"
  /** Calcul prevu ou en cours, et la bascule de la carte graphique vers le serveur */
  engine?: RenderEngine
  gpuFallback?: boolean
  /** Rang dans la file du serveur, pour une demande en attente sur le serveur */
  position?: number
  /** Message d'echec, pour une demande echouee */
  error?: string
  /** La finition IA (FIA) : le moteur, son echec (image de Blender), et l'etape en cours (finition : Blender a fini) */
  aiModel?: RenderAiModel | null
  aiFallback?: boolean
  stage?: "finition"
}
